/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react"
import { memo, type ReactNode } from "react"
import { theme } from "../../styles/theme/theme"

export interface PageHeaderProps {
  /** String → rendered as a styled <h1>. ReactNode → rendered as-is, so callers
   *  can put a tab strip or other custom element in the title position. */
  title: string | ReactNode
  /** Rendered inline immediately after the title (e.g. a selector or badge). */
  titleAddon?: ReactNode
  description?: string
  /** Rendered on the far-right side of the header. */
  actions?: ReactNode
}

export const PageHeader = memo<PageHeaderProps>(({ title, titleAddon, description, actions }) => (
  <div
    css={css`
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
      padding: 0.75rem 0.75rem 0;
      flex-shrink: 0;
      background: ${theme.colors.background};
    `}
  >
    <div css={css`min-width: 0; flex: 1;`}>
      <div css={css`display: flex; align-items: center; gap: 0.5rem; min-width: 0;`}>
        {typeof title === "string" ? (
          <h1
            css={css`
              font-size: 1.125rem;
              font-weight: 600;
              color: ${theme.colors.foreground};
              margin: 0;
              white-space: nowrap;
              flex-shrink: 0;
            `}
          >
            {title}
          </h1>
        ) : (
          title
        )}
        {titleAddon}
      </div>
      {description && (
        <p
          css={css`
            font-size: 0.8125rem;
            color: ${theme.colors.mutedForeground};
            margin: 0.125rem 0 0;
          `}
        >
          {description}
        </p>
      )}
    </div>
    {actions && (
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-shrink: 0;
        `}
      >
        {actions}
      </div>
    )}
  </div>
))

PageHeader.displayName = "PageHeader"
